import { AsyncPipe } from '@angular/common';
import { Component, inject, Inject } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatCardSubtitle } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  Association,
  CardAction,
  CommonAssociationCardContentComponent,
  CrmDependencies,
  CrmFieldService,
  CrmInjectionToken,
  formatAsReadonlyPhone,
  PAGE_ROUTES,
  parsePhoneNumber,
  StandardExternalIds,
  StandardIds,
  CrmObjectDisplayService,
  ObjectType,
  AssociationCardData,
  CrmObjectAssociationPanelComponent,
  BrandingService,
  COMPANY_PROFILE_CARD_DATA_TOKEN,
} from '@galaxy/crm/static';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, combineLatest, firstValueFrom, of, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  AccessBusinessAppComponent,
  AccessBusinessAppService,
} from '../association-cards/access-business-app/access-business-app.component';
import { AppAccess } from '../association-cards/access-business-app/access-business-app.service';

export interface ContactAssociationCardData extends AssociationCardData {
  phone?: string;
  linkedInUrl?: string;
  jobTitle?: string;
  email?: string;
  businessAppAccess?: AppAccess;
}

@Component({
  selector: 'app-pcc-crm-company-to-contact-associations-card',
  templateUrl: './pcc-company-to-contact-associations-card.component.html',
  styleUrls: ['./pcc-company-to-contact-associations-card.component.scss'],
  imports: [
    CrmObjectAssociationPanelComponent,
    CommonAssociationCardContentComponent,
    AccessBusinessAppComponent,
    MatCardSubtitle,
    MatIcon,
    MatIconButton,
    AsyncPipe,
  ],
  providers: [{ provide: Window, useValue: window }, BrandingService, AccessBusinessAppService],
})
export class PccCompanyToContactAssociationsCardComponent {
  data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  id = this.data.company_id;
  name = this.data.name;
  objectType: ObjectType = 'Company';

  protected readonly showCampaignAction$ = this.config.company?.showCampaigns$ || of(true);
  private extraActions$$ = new BehaviorSubject<CardAction[]>([]);
  protected readonly customActions$ = combineLatest([this.showCampaignAction$, this.extraActions$$]).pipe(
    map(([showCampaigns, extraActions]) =>
      showCampaigns && !!this.config.contact?.services?.campaignService
        ? [this.campaignAction, this.editContactPageAction, ...extraActions]
        : [this.editContactPageAction, ...extraActions],
    ),
  );
  private readonly campaignAction: CardAction = {
    label: this.translateService.instant('ASSOCIATIONS.CAMPAIGNS.ADD_TO_CAMPAIGN'),
    callback: (data: AssociationCardData) => {
      this.openCampaignDialog(data.objectId || '');
    },
  };

  openCampaignDialog(id: string): void {
    this.config.contact?.services?.campaignService?.navigateToSendCampaign([id]);
  }

  private readonly editContactPageAction: CardAction = {
    label: this.translateService.instant('ASSOCIATIONS.CONTACT.ACTIONS.EDIT'),
    callback: (data: AssociationCardData) => {
      this.openEditContactPage(data.objectId || '');
    },
    icon: 'open_in_new',
  };

  async openEditContactPage(id: string) {
    const routePrefix = await firstValueFrom(this.config.routePrefix$);
    let newPath = `${routePrefix}/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.EDIT}`.replace(
      ':crmObjectId',
      id,
    );
    newPath = newPath + `?returnUrl=${this.window?.location.pathname}`;
    this.window?.open(newPath, '_blank')?.focus();
  }

  protected readonly cardFunction = (
    associations: Association[],
    routePrefix: string,
  ): ContactAssociationCardData[] => {
    return associations.map((association) => {
      const fullName = this.objectDisplayService.getObjectDisplayName('Contact', association.associatedObject || {});

      const phoneParsed = parsePhoneNumber(
        this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardExternalIds.PhoneNumber)
          ?.stringValue || '',
      );
      const businessAppAccess = this.accessBusinessAppService.getAppAccess(
        this.dialog,
        association.associatedObject.crmObjectId || '',
        this.id || '',
        fullName,
        this.name,
      );

      businessAppAccess?.actions$?.pipe(take(1)).subscribe((actions) => {
        this.extraActions$$.next(actions);
      });

      return {
        associationId: association.associationId,
        objectId: association.associatedObject.crmObjectId,
        name: fullName,
        profileUrl: `${routePrefix}/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`.replace(
          ':crmObjectId',
          association.associatedObject.crmObjectId || '',
        ),
        icon: 'user',
        tags: (association.tags || []).filter((tag) => tag !== 'Primary'),
        phone: formatAsReadonlyPhone(phoneParsed),
        email: this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardExternalIds.Email)
          ?.stringValue,
        linkedInUrl: this.formatAsLinkedinUrl(
          this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.ContactLinkedInURL)
            ?.stringValue,
        ),
        jobTitle: this.fieldService.getFieldValueFromCrmObject(
          association.associatedObject,
          StandardIds.ContactJobTitle,
        )?.stringValue,
        businessAppAccess: businessAppAccess,
      } as ContactAssociationCardData;
    });
  };

  formatAsLinkedinUrl(url?: string): string | undefined {
    if (!url) return undefined;
    if (url.startsWith('https://www.')) {
      return url;
    }
    if (url.startsWith('www.')) {
      return 'https://' + url;
    }
    return 'https://www.' + url;
  }

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly dialog: MatDialog,
    private translateService: TranslateService,
    private readonly fieldService: CrmFieldService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    private window: Window,
    private clipboard: Clipboard,
    private snackbarService: SnackbarService,
    private accessBusinessAppService: AccessBusinessAppService,
  ) {}

  openMailWindow(email: string): void {
    this.window.open(
      `mailto:${email}`,
      'targetWindow',
      'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=900,height=600',
    );
  }
  copyToClipboard(email: string): void {
    this.clipboard.copy(email);
    this.snackbarService.openSuccessSnack('COMMON.SNACKBAR_MESSAGE.COPY_SUCCESS', { action: 'Close' });
  }
}
